import React, { useEffect, useRef, useState } from "react";
import DOMPurify from "dompurify";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import { useSubmitTodayDailyQuestion, useTodayDailyQuestion } from "../hooks/useDailyQuestions";
import { useQuestionBankQuestionReference } from "../hooks/useQuestionBankPractice";
import { getStoredUser } from "../utils/authStorage";
import "./DailyQuestionPopup.css";
import { CONTENT_ROUTES } from "./contentRoutes";

const normaliseLanguage = (value) => String(value || "EN").toUpperCase().startsWith("HI") ? "HI" : "EN";
const sanitise = (value) => DOMPurify.sanitize(String(value || ""));

const localisedValue = (item, field, language) => {
  const suffix = language === "HI" ? "hi" : "en";
  const fallback = suffix === "hi" ? "en" : "hi";
  return item?.[`${field}_${suffix}`] || item?.[field] || item?.[`${field}_${fallback}`] || "";
};

const splitBilingualHtml = (value) => {
  const parts = String(value || "").split(/<br\s*\/?>(?:\s*)/i);
  return { primary: sanitise(parts.shift()), secondary: sanitise(parts.join("<br />")) };
};

const DrawerShimmer = () => (
  <div className="daily-question-popup-shimmer" aria-label="Loading today’s question" aria-busy="true">
    <span className="daily-question-popup-shimmer__line daily-question-popup-shimmer__line--title" />
    <span className="daily-question-popup-shimmer__line" />
    <span className="daily-question-popup-shimmer__line daily-question-popup-shimmer__line--short" />
    {Array.from({ length: 4 }, (_, index) => <span className="daily-question-popup-shimmer__option" key={index} />)}
  </div>
);

const optionTone = (option, question, selectedOptionId, isAnswered) => {
  if (!isAnswered) return Number(option?.option_id) === Number(selectedOptionId) ? "selected" : "neutral";
  const isCorrect = String(option?.is_option_correct || "").toUpperCase() === "Y" || Number(option?.option_id) === Number(question?.correct_option_id || 0);
  const isSelected = Number(option?.user_selected_option || 0) === 1 || Number(option?.option_id) === Number(question?.attempted_option_id || 0);
  if (isCorrect) return "correct";
  return isSelected ? "wrong" : "neutral";
};

const DailyQuestionDrawer = ({ isOpen, onClose }) => {
  const navigate = useNavigate();
  const closeTimerRef = useRef(null);
  const drawerRef = useRef(null);
  const dragStartY = useRef(0);
  const currentDragY = useRef(0);
  const isDragging = useRef(false);
  const [selectedOptionId, setSelectedOptionId] = useState(0);
  const [isExplanationOpen, setIsExplanationOpen] = useState(true);
  const [isPYPOpen, setIsPYPOpen] = useState(true);
  const storedUser = getStoredUser();
  const language = normaliseLanguage(storedUser?.content_language || storedUser?.language);
  const todayQuery = useTodayDailyQuestion(language, { enabled: isOpen });
  const submitAnswer = useSubmitTodayDailyQuestion(language);
  const question = todayQuery.data || null;
  const options = Array.isArray(question?.question_option) ? question.question_option : [];
  const isAnswered = Number(question?.is_question_answered || 0) === 1;
  const pypReference =
    localisedValue(question, "pyp", language) ||
    localisedValue(question, "pyp_reference", language) ||
    localisedValue(question, "previous_year_question", language);
  const pyqMappings = Array.isArray(question?.pyq_mappings) ? question.pyq_mappings : [];
  const hasPyq = Number(question?.is_pyq || 0) === 1 || Boolean(pypReference) || pyqMappings.length > 0;
  const selectedOption = options.find(
    (option) => Number(option?.user_selected_option || 0) === 1 || Number(option?.option_id) === Number(question?.attempted_option_id || 0)
  );
  const isCorrect =
    String(question?.is_answer_correct || "").toUpperCase() === "Y" ||
    String(selectedOption?.is_option_correct || "").toUpperCase() === "Y";
  const referenceQuery = useQuestionBankQuestionReference(
    {
      question_id: Number(question?.question_id || 0),
      language,
      is_correct: isCorrect ? 1 : 0,
    },
    { enabled: isAnswered && Number(question?.question_id || 0) > 0 }
  );
  const explanation =
    referenceQuery.data?.additional_info ||
    localisedValue(referenceQuery.data, "ref_message", language) ||
    referenceQuery.data?.ref_message_en ||
    referenceQuery.data?.ref_message_hi ||
    referenceQuery.data?.description ||
    localisedValue(question, "explanation", language) ||
    localisedValue(question, "ref_message", language);

  useEffect(() => {
    if (!isOpen) return;
    setSelectedOptionId(Number(question?.attempted_option_id || 0));
    setIsExplanationOpen(true);
    setIsPYPOpen(true);
  }, [isOpen, question?.daily_question_id, question?.attempted_option_id]);

  useEffect(() => {
    if (!isOpen) return undefined;
    const previousFocus = document.activeElement;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    drawerRef.current?.focus();
    const handleKeyDown = (event) => {
      if (event.key === "Escape") onClose();
      if (event.key === "Tab") {
        const controls = Array.from(drawerRef.current?.querySelectorAll('button:not(:disabled), [href], [tabindex="0"]') || [])
          .filter((element) => window.getComputedStyle(element).display !== "none");
        const first = controls[0];
        const last = controls[controls.length - 1];
        if (event.shiftKey && (document.activeElement === first || document.activeElement === drawerRef.current)) {
          event.preventDefault();
          last?.focus();
        } else if (!event.shiftKey && document.activeElement === last) {
          event.preventDefault();
          first?.focus();
        }
      }
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => {
      window.clearTimeout(closeTimerRef.current);
      isDragging.current = false;
      document.body.style.overflow = previousOverflow;
      document.removeEventListener("keydown", handleKeyDown);
      previousFocus?.focus();
    };
  }, [isOpen, onClose]);

  const handleDragStart = (event) => {
    if (window.innerWidth > 575 || (event.button !== undefined && event.button !== 0)) return;
    window.clearTimeout(closeTimerRef.current);
    isDragging.current = true;
    dragStartY.current = event.clientY;
    currentDragY.current = 0;
    event.currentTarget.setPointerCapture?.(event.pointerId);
    drawerRef.current.style.transition = "none";
  };

  const handleDragMove = (event) => {
    if (!isDragging.current) return;
    currentDragY.current = Math.max(0, event.clientY - dragStartY.current);
    drawerRef.current.style.transform = `translateY(${currentDragY.current}px)`;
  };

  const handleDragEnd = (event) => {
    if (!isDragging.current) return;
    isDragging.current = false;
    const shouldClose = event.type !== "pointercancel" && currentDragY.current > 100;
    drawerRef.current.style.transition = "transform 0.25s ease";
    drawerRef.current.style.transform = shouldClose ? "translateY(100%)" : "translateY(0)";
    if (shouldClose) closeTimerRef.current = window.setTimeout(onClose, 250);
    currentDragY.current = 0;
  };

  const handleOptionSelect = (optionId) => {
    if (!question?.daily_question_id || !optionId || submitAnswer.isPending || isAnswered) return;
    setSelectedOptionId(Number(optionId));
    submitAnswer.mutate({
      daily_question_id: Number(question.daily_question_id),
      user_answer_id: Number(optionId),
      is_guess: 0,
    });
  };

  if (!isOpen) return null;

  return createPortal(
    <div className="daily-question-overlay" onClick={onClose}>
      <div ref={drawerRef} role="dialog" aria-modal="true" aria-labelledby="dashboard-daily-question-title" tabIndex={-1} className="daily-question-drawer" onClick={(event) => event.stopPropagation()}>
        <button type="button" className="daily-question-drag-handle" aria-label="Close daily question or drag down to dismiss" onPointerDown={handleDragStart} onPointerMove={handleDragMove} onPointerUp={handleDragEnd} onPointerCancel={handleDragEnd} onClick={(event) => { if (event.detail === 0) onClose(); }}><span aria-hidden="true" /></button>
        <button type="button" className="daily-question-close" onClick={onClose} aria-label="Close"><i className="bi bi-x" /></button>

        <div className="daily-question-header">
          {todayQuery.isLoading ? (
            <div className="daily-question-header-shimmer" id="dashboard-daily-question-title" aria-label="Loading today’s question">
              <div className="daily-question-header-shimmer__top"><span /><span /></div>
              <span className="daily-question-header-shimmer__title" />
              <span className="daily-question-header-shimmer__subtitle" />
            </div>
          ) : (
            <>
              <div className="daily-question-header-top">
                <div className="daily-challenge-badge"><span className="daily-challenge-star">*</span>Daily Challenge</div>
                <button type="button" className="daily-view-all" onClick={() => { onClose(); navigate(CONTENT_ROUTES.dailyQuestions); }}>View All</button>
              </div>
              <h2 id="dashboard-daily-question-title">Today Question</h2>
              <p>{isAnswered ? "Today’s answer has been submitted. Review your result below." : "Attempt today’s question and keep your streak moving."}</p>
            </>
          )}
        </div>

        <div className="daily-question-content daily-Modal">
          {todayQuery.isLoading ? <DrawerShimmer /> : null}
          {todayQuery.isError ? <div className="daily-question-popup-status daily-question-popup-status--error">{todayQuery.error?.response?.data?.message || todayQuery.error?.message || "Unable to load today’s question."}</div> : null}
          {!todayQuery.isLoading && !todayQuery.isError && !question ? <div className="daily-question-popup-status">No daily question is available today.</div> : null}
          {!todayQuery.isLoading && !todayQuery.isError && question ? (
            <div className="daily-popup-question-scroll">
              <div className="daily-popup-qbank-questionBlock">
                {(() => {
                  const questionText = splitBilingualHtml(localisedValue(question, "question_name", language));
                  return <><h2 className="daily-popup-qbank-question" dangerouslySetInnerHTML={{ __html: questionText.primary }} />{questionText.secondary ? <p className="daily-popup-qbank-questionSub" dangerouslySetInnerHTML={{ __html: questionText.secondary }} /> : null}</>;
                })()}
              </div>

              <div className="daily-popup-qbank-options" role="list" aria-label="Answer options">
                {options.map((option, index) => {
                  const optionText = splitBilingualHtml(localisedValue(option, "option_name", language));
                  const tone = optionTone(option, question, selectedOptionId, isAnswered);
                  const isSelected = Number(option.option_id) === Number(selectedOptionId);
                  return <button key={option.option_id || index} type="button" disabled={isAnswered || submitAnswer.isPending} className={`daily-popup-qbank-option daily-popup-qbank-option--${tone}`} aria-label={`Option ${String.fromCharCode(65 + index)}`} onClick={() => handleOptionSelect(Number(option.option_id))}>
                    <span className="daily-popup-qbank-option__badge">{String.fromCharCode(65 + index)}</span>
                    <span className="daily-popup-qbank-option__copy"><span className="daily-popup-qbank-option__english" dangerouslySetInnerHTML={{ __html: optionText.primary }} />{optionText.secondary ? <span className="daily-popup-qbank-option__hindi" dangerouslySetInnerHTML={{ __html: optionText.secondary }} /> : null}</span>
                    {!isAnswered && isSelected ? <span className="daily-popup-qbank-option__check"><i className="bi bi-check2" /></span> : null}
                  </button>;
                })}
              </div>

              {submitAnswer.isError ? <p className="daily-question-popup-status daily-question-popup-status--error">{submitAnswer.error?.response?.data?.message || submitAnswer.error?.message || "Unable to submit your answer."}</p> : null}

              {hasPyq ? <section className="daily-popup-qbank-sideCard mt-3"><div className="daily-popup-qbank-sideCard__head"><h3>PYP</h3><button type="button" className="daily-popup-qbank-sideCard__toggle" aria-label="Toggle PYP" onClick={() => setIsPYPOpen((current) => !current)}><i className={`bi ${isPYPOpen ? "bi-chevron-up" : "bi-chevron-down"}`} /></button></div>{isPYPOpen ? <div className="daily-popup-qbank-sideCard__body"><div className="daily-popup-qbank-sideCard__subtitle">{pyqMappings.length ? pyqMappings.map((item, index) => <span key={item?.id || `${item?.master_exam}-${item?.year}-${index}`}>{item?.master_exam || item?.exam_name || "Previous Year"}{item?.year ? ` ${item.year}` : ""}</span>) : <div dangerouslySetInnerHTML={{ __html: sanitise(pypReference) }} />}</div></div> : null}</section> : null}
              {isAnswered && (referenceQuery.isLoading || explanation) ? <section className="daily-popup-qbank-sideCard mt-3"><div className="daily-popup-qbank-sideCard__head"><h3>Explanation</h3><button type="button" className="daily-popup-qbank-sideCard__toggle" aria-label="Toggle explanation" onClick={() => setIsExplanationOpen((current) => !current)}><i className={`bi ${isExplanationOpen ? "bi-chevron-up" : "bi-chevron-down"}`} /></button></div>{isExplanationOpen ? <div className="daily-popup-qbank-sideCard__body"><div className="daily-popup-qbank-sideCard__subtitle">{referenceQuery.isLoading ? <span className="daily-question-reference-shimmer" role="status" aria-label="Loading explanation" /> : <span dangerouslySetInnerHTML={{ __html: sanitise(explanation) }} />}</div></div> : null}</section> : null}
            </div>
          ) : null}
        </div>
      </div>
    </div>,
    document.body
  );
};

export default DailyQuestionDrawer;
